/**
 * Contexto de sesión y su hook.
 *
 * Separado de `AuthProvider.tsx` por la misma convención que `useApi.ts`: un
 * `.tsx` exporta componentes y nada más.
 */
import { createContext, useContext } from 'react';

import type { SessionEndReason } from '@/api/client';
import type { Role } from '@/api/types';
import type { AccessClaims } from './claims';

export interface LoginInput {
  /** Slug de la marca. El correo es único **por tenant**, no globalmente. */
  tenant: string;
  email: string;
  password: string;
}

export interface AuthValue {
  /** Claims del access token, o `null` si no hay sesión. */
  claims: AccessClaims | null;
  isAuthenticated: boolean;
  /** Por qué terminó la última sesión. Lo muestra la pantalla de login. */
  endedReason: SessionEndReason | null;
  /** `true` mientras el rol actual alcance `minimum`. */
  can: (minimum: Role) => boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: (options?: { allSessions?: boolean }) => Promise<void>;
}

export const AuthContext = createContext<AuthValue | null>(null);

/**
 * Sesión actual: claims, rol, login y logout.
 *
 *     const { claims, can, logout } = useAuth();
 *     if (can('analyst')) { … }
 *
 * `can` es **ergonomía**, no seguridad: quien manda es la API. Ver
 * `auth/claims.ts`.
 */
export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth() fuera de <AuthProvider>. Revisá el árbol de providers.');
  }
  return value;
}
